import { ReactNode, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "src/store/store"
import {
    TipoOrdenPanelCocina,
    EstadosOrdenHistorial,
    EstadosComandaHistorial,
    useGetOrdenesEnEntregaQuery,
    useGetOrdenesEnPreparacionQuery,
    useListenCheckOrdenesSubscription,
} from "src/gql/schema"
import {
    setOrdersInPreparation,
    startLoadingOrdersInPreparation,
} from "src/store/ordersInPreparation/ordersInPreparationSlice"
import { setOrdersToDeliver, startLoadingOrdersToDeliver } from "src/store/ordersToDeliver/ordersToDeliverSlice"
import { useProfile } from "../hooks/useProfile"
import { requestWsReconnect } from "src/graphql"

const OrdersProvider = ({ children }: { children: ReactNode }) => {
    const dispatch = useDispatch()
    const { hotel_id } = useProfile()

    const { ordersInPreparation } = useSelector((root: RootState) => root.ordersInPreparation)
    const { data: ordenesEnPreparacionQueryData, refetch: refetchPreparacion } = useGetOrdenesEnPreparacionQuery({
        variables: { hotel_id },
        notifyOnNetworkStatusChange: true,
    })
    const { ordersToDeliver } = useSelector((root: RootState) => root.ordersToDeliver)
    const { data: ordenesEnEntregaQueryData, refetch: refetchEntrega } = useGetOrdenesEnEntregaQuery({
        variables: { hotel_id },
        notifyOnNetworkStatusChange: true,
    })

    const { data } = useListenCheckOrdenesSubscription({ variables: { hotel_id } })
    const newOrderAudio = useRef(new Audio("https://articulos.nyc3.cdn.digitaloceanspaces.com/easyroom/pedido%20easyroom%20grave.mp3"))
    const audioUnlocked = useRef(false)

    useEffect(() => {
        newOrderAudio.current.load()
        const events = ["click", "keydown"] as const
        const unlock = () => {
            if (audioUnlocked.current) return
            newOrderAudio.current
                .play()
                .then(() => {
                    newOrderAudio.current.pause()
                    newOrderAudio.current.currentTime = 0
                    audioUnlocked.current = true
                })
                .catch(() => undefined)
            events.forEach((e) => document.removeEventListener(e, unlock))
        }
        events.forEach((e) => document.addEventListener(e, unlock))
        return () => events.forEach((e) => document.removeEventListener(e, unlock))
    }, [])

    function sortOrdersByDate(orders?: any[]) {
        if (!Array.isArray(orders)) {
            return []
        }
        return [...orders].sort((a, b) => {
            const dateA = a.comanda?.fecha_registro ? new Date(a.comanda.fecha_registro).getTime() : 0
            const dateB = b.comanda?.fecha_registro ? new Date(b.comanda.fecha_registro).getTime() : 0
            return dateA - dateB
        })
    }

    useEffect(() => {
        if (!ordenesEnEntregaQueryData && !ordenesEnEntregaQueryData) {
            dispatch(startLoadingOrdersInPreparation())
            dispatch(startLoadingOrdersToDeliver())
        }

        if (ordenesEnPreparacionQueryData?.ordenes) {
            dispatch(setOrdersInPreparation(sortOrdersByDate(ordenesEnPreparacionQueryData?.ordenes)))
        }
        if (ordenesEnEntregaQueryData?.ordenes) {
            dispatch(setOrdersToDeliver(sortOrdersByDate(ordenesEnEntregaQueryData?.ordenes)))
        }
    }, [ordenesEnPreparacionQueryData, ordenesEnEntregaQueryData])

    useEffect(() => {
        if (data?.checkOrdenes) {
            // Funciones de órdenes en preparación
            const filterKeyEP =
                data?.checkOrdenes?.orden_en_preparacion?.tipo === TipoOrdenPanelCocina.Comanda
                    ? "virtual_comanda_id"
                    : "orden_id"

            if (data.checkOrdenes.orden_en_preparacion?.accion === "actualizar") {
                const { orden_en_preparacion } = data.checkOrdenes
                const ordenEnPreparacion = orden_en_preparacion?.orden
                const comandaEnPreparacion = ordenEnPreparacion?.comanda

                const updatedOrders = ordersInPreparation.map((order) => {
                    if (order[filterKeyEP] === ordenEnPreparacion[filterKeyEP]) {
                        if (
                            ordenEnPreparacion?.estado_orden === EstadosOrdenHistorial.Cancelada ||
                            comandaEnPreparacion?.estado_comanda === EstadosComandaHistorial.Cancelada
                        ) {
                            const updatedOrder = {
                                ...order,
                                comanda: comandaEnPreparacion,
                                estado_orden: "cancelada",
                            }
                            return updatedOrder
                        } else {
                            return ordenEnPreparacion
                        }
                    }
                    return order
                })
                dispatch(setOrdersInPreparation(updatedOrders as any))
            }
            if (data.checkOrdenes.orden_en_preparacion?.accion === "remover") {
                const updatedOrders = ordersInPreparation.filter((order) => {
                    return order[filterKeyEP] !== data?.checkOrdenes?.orden_en_preparacion?.orden[filterKeyEP]
                })
                dispatch(setOrdersInPreparation(updatedOrders as any))
            }
            if (data.checkOrdenes.orden_en_preparacion?.accion === "agregar") {
                const { orden_en_preparacion } = data.checkOrdenes
                const newOrder = orden_en_preparacion?.orden

                let isOrderExisting = false

                if (orden_en_preparacion.tipo === TipoOrdenPanelCocina.Orden) {
                    isOrderExisting = ordersInPreparation.some((order) => order.orden_id === newOrder.orden_id)
                }

                if (!isOrderExisting) {
                    const updatedOrders = [...ordersInPreparation, newOrder]
                    dispatch(setOrdersInPreparation(updatedOrders as any))
                    console.log(newOrder?.usuario_id)
                    if (newOrder?.usuario_id === "e2921491-dfa2-4615-b9f3-4a9129dd7450" || newOrder?.usuario_id === "ffb2168b-a25d-4f48-b1ef-5b088e6bdff3") {
                        newOrderAudio.current.currentTime = 0
                        newOrderAudio.current.play().catch(() => undefined)
                    }
                }
            }

            // Funciones de órdenes por entregar
            const filterKeyPE =
                data?.checkOrdenes?.orden_por_entregar?.tipo === TipoOrdenPanelCocina.Comanda
                    ? "virtual_comanda_id"
                    : "orden_id"

            if (data.checkOrdenes.orden_por_entregar?.accion === "agregar") {
                const updatedOrders = ordersToDeliver.map((order) => {
                    if (order[filterKeyPE] === data?.checkOrdenes?.orden_por_entregar?.orden[filterKeyPE]) {
                        return data?.checkOrdenes?.orden_por_entregar?.orden
                    }
                    return order
                })
                const isOrderAlreadyInArray = updatedOrders.some(
                    (order) => order[filterKeyPE] === data?.checkOrdenes?.orden_por_entregar?.orden[filterKeyPE]
                )
                if (!isOrderAlreadyInArray) {
                    updatedOrders.push(data?.checkOrdenes?.orden_por_entregar?.orden)
                }
                dispatch(setOrdersToDeliver(updatedOrders as any))
            }
            if (data.checkOrdenes.orden_por_entregar?.accion === "actualizar") {
                const { orden_por_entregar } = data.checkOrdenes
                const ordenPorEntregar = orden_por_entregar?.orden
                const comandaPorEntregar = ordenPorEntregar?.comanda

                const existingOrderIndex = ordersToDeliver.findIndex(
                    (order) => order[filterKeyPE] === ordenPorEntregar[filterKeyPE]
                )

                let updatedOrders = ordersToDeliver

                if (existingOrderIndex !== -1) {
                    updatedOrders = ordersToDeliver.map((order, index) => {
                        if (index === existingOrderIndex) {
                            return ordenPorEntregar?.estado_orden === EstadosOrdenHistorial.Cancelada ||
                                comandaPorEntregar?.estado_comanda === EstadosComandaHistorial.Cancelada
                                ? { ...order, estado_orden: "cancelada", comanda: comandaPorEntregar }
                                : ordenPorEntregar
                        }
                        return order
                    })
                } else if (filterKeyPE === "virtual_comanda_id") {
                    /**
                     * La comanda aun no existe en por_entregar asi que se agrega
                     * esto solo debe pasar para comandas
                     */
                    updatedOrders = [...ordersToDeliver, ordenPorEntregar]
                }

                dispatch(setOrdersToDeliver(updatedOrders as any))
            }
            if (data.checkOrdenes.orden_por_entregar?.accion === "remover") {
                const updatedOrders = ordersToDeliver.filter((order) => {
                    return order[filterKeyPE] !== data?.checkOrdenes?.orden_por_entregar?.orden[filterKeyPE]
                })
                dispatch(setOrdersToDeliver(updatedOrders as any))
            }
        }
    }, [data])

    useEffect(() => {
        const resync = async (force: boolean) => {
            if (document.visibilityState !== "visible") return
            // La decisión es sincrónica y va antes de cualquier `await`, para que los
            // providers compartan la misma ronda de resync.
            const reconnection = requestWsReconnect({ force })
            // Solo se resincroniza si ya pasó el intervalo mínimo o si la conexión está
            // caída; si no, no se hace nada: ni socket nuevo, ni skeleton, ni refetch
            // (ver requestWsReconnect).
            if (!reconnection) return

            dispatch(startLoadingOrdersInPreparation())
            dispatch(startLoadingOrdersToDeliver())
            // Se espera la confirmación de la conexión ANTES de refetchear: así, si algo
            // cambia mientras el refetch está en curso, la suscripción (ya viva) lo
            // recibe y esa actualización llega después, sin que el refetch la pise con un
            // snapshot más viejo.
            await reconnection
            refetchPreparacion()
            refetchEntrega()
        }
        const onVisibilityChange = () => resync(false)
        const onOnline = () => resync(true)
        document.addEventListener("visibilitychange", onVisibilityChange)
        window.addEventListener("online", onOnline)
        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange)
            window.removeEventListener("online", onOnline)
        }
    }, [refetchPreparacion, refetchEntrega])

    return <>{children}</>
}

export default OrdersProvider
